import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  CheckCircle,
  Download,
  Phone,
  Shield,
  Zap,
  Thermometer,
  Volume2,
  Wifi,
  Leaf,
  Award,
  Sparkles,
} from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import {
  getProduct,
  getBrand,
  getCategory,
  getProductsByCategory,
} from '../../data/products';

const featureIcon = (title) => {
  if (/COP|Efficiency/i.test(title)) return Zap;
  if (/Inverter/i.test(title)) return Sparkles;
  if (/Touch Screen/i.test(title)) return Wifi;
  if (/Refrigerant/i.test(title)) return Leaf;
  if (/Tank|Corrosion/i.test(title)) return Shield;
  if (/Wi-Fi|App/i.test(title)) return Wifi;
  if (/Modes/i.test(title)) return Thermometer;
  if (/Silent|Quiet/i.test(title)) return Volume2;
  return Award;
};

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const product = getProduct(productSlug);

  if (!product) {
    return <Navigate to="/brands" replace />;
  }

  const brand = getBrand(product.brand);
  const category = getCategory(product.category);
  const relatedProducts = getProductsByCategory(product.category).filter(
    (p) => p.slug !== product.slug
  );

  const brochureUrl = product.brochureUrl || brand.brochureUrl || null;
  const warrantyUrl = product.warrantyUrl || brand.warrantyUrl || null;
  const headlineShort = (product.headlineSpec?.label || 'Spec').split(' ')[0];

  // ── Hero variant selection ──
  // Use the light "photoshoot" hero when the product opts in via `heroVariant: 'light'`
  // OR when the image is a dark/transparent render (`imageBlendMode: 'normal'`).
  const useLightHero =
    product.heroVariant === 'light' || product.imageBlendMode === 'normal';

  // Floating overlay content for the light hero — data-driven with sensible defaults.
  const lucideMap = { Shield, Leaf, Zap, Award, Wifi, Volume2, Thermometer, Sparkles };
  const heroStatCard =
    product.heroStatCard || {
      label: 'Manufacturer Warranty',
      value: (product.warranty?.[0]?.term || '').match(/\d+/)?.[0] || '',
      unit: '-yr',
      sub: product.warranty?.[0]?.component
        ? `${product.warranty[0].component} · ${(product.warranty[1]?.component || '').toLowerCase()}*`
        : '',
      icon: 'Shield',
    };
  const heroChip = product.heroChip || {
    label: product.heroPills?.[0]?.label || 'Featured',
    icon: product.heroPills?.[0]?.icon || 'Award',
  };
  const HeroStatIcon = lucideMap[heroStatCard.icon] || Shield;
  const HeroChipIcon = lucideMap[heroChip.icon] || Award;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://supaenergyhub.com.au/' },
        { '@type': 'ListItem', position: 2, name: category.name, item: `https://supaenergyhub.com.au/products/${category.slug}` },
        { '@type': 'ListItem', position: 3, name: product.name, item: `https://supaenergyhub.com.au/products/${category.slug}/${product.slug}` },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      model: product.model,
      category: category.name,
      brand: { '@type': 'Brand', name: brand.name },
      description: product.description,
      image: brand.logo,
      additionalProperty: product.specs.map((s) => ({
        '@type': 'PropertyValue',
        name: s.label,
        value: s.value,
      })),
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title={`${product.name} (${product.model}) — ${category.name}`}
        description={`${product.description} ${product.headlineSpec.value}${product.headlineSpec.unit} tank, R290 refrigerant. Installed by Supa Energy Hub with rebates applied upfront.`}
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Breadcrumb + Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <nav
              className="flex items-center gap-2 text-sm text-gray-500 mb-8 flex-wrap"
              data-testid="product-breadcrumb"
            >
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to={`/products/${category.slug}`} className="hover:text-[#3D5A47]">
                {category.name}
              </Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to={`/brands/${brand.slug}`} className="hover:text-[#3D5A47]">
                {brand.name}
              </Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">{product.series} {product.headlineSpec.value}{product.headlineSpec.unit}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* Left: Product info */}
              <div className="lg:col-span-7">
                <div className="flex items-center gap-3 mb-4">
                  <img src={brand.logo} alt={brand.name} className="h-8 w-auto" />
                  <span className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                    · {product.series}
                  </span>
                </div>

                <h1 className="heading-1 mb-3 text-[#3D5A47] leading-tight" data-testid="product-name">
                  {product.name}
                </h1>
                <p className="text-sm text-gray-400 font-mono mb-4" data-testid="product-model">
                  Model {product.model}
                </p>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">{product.tagline}</p>
                <p className="text-gray-600 leading-relaxed mb-6">{product.description}</p>

                {/* Quick stats pills (derived from product specs) */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {(product.heroPills || []).map((pill, i) => {
                    const Icon = pill.icon === 'Leaf' ? Leaf
                      : pill.icon === 'Volume2' ? Volume2
                      : pill.icon === 'Wifi' ? Wifi
                      : pill.icon === 'Zap' ? Zap
                      : null;
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3D5A47] bg-[#B5D43B]/20 border border-[#B5D43B]/40 rounded-full px-3 py-1.5"
                      >
                        {Icon && <Icon className="w-3.5 h-3.5" />} {pill.label}
                      </span>
                    );
                  })}
                  {product.rebateEligible && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#3D5A47] rounded-full px-3 py-1.5">
                      <Award className="w-3.5 h-3.5" /> Rebate Eligible
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-3">
                  <Link to="/get-estimate">
                    <Button
                      className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold"
                      data-testid="product-cta-estimate"
                    >
                      Get a Free Estimate <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  {brochureUrl && (
                    <a
                      href={brochureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="product-brochure-download"
                    >
                      <Button variant="outline" className="gap-2 border-[#3D5A47] text-[#3D5A47] hover:bg-[#3D5A47] hover:text-white">
                        <Download className="w-4 h-4" /> Download Brochure (PDF)
                      </Button>
                    </a>
                  )}
                  {warrantyUrl && (
                    <a
                      href={warrantyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="product-warranty-download"
                    >
                      <Button variant="outline" className="gap-2 border-gray-300 text-gray-700 hover:bg-gray-100">
                        <Download className="w-4 h-4" /> Warranty (PDF)
                      </Button>
                    </a>
                  )}
                  <a href="tel:0478453548" data-testid="product-cta-call">
                    <Button variant="outline" className="gap-2 border-gray-300">
                      <Phone className="w-4 h-4" /> Call 0478 453 548
                    </Button>
                  </a>
                </div>
              </div>

              {/* Right: Product hero — two-variant design.
                  Light-bg renders (heat pumps, Fox ESS) use the dark-green Supa hero.
                  Dark/transparent renders (WHES) use a light "product photoshoot" card. */}
              <div className="lg:col-span-5 flex justify-center">
                {useLightHero ? (
                  // ── Light hero variant (for dark/transparent renders) ──
                  <div className="relative w-full max-w-md">
                    <div
                      className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl flex border border-[#3D5A47]/10"
                      style={{
                        background:
                          'radial-gradient(circle at 50% 30%, #ffffff 0%, #f6f8f3 45%, #e3ecdc 100%)',
                      }}
                      data-testid="product-tank-hero"
                    >
                      {/* Subtle green dot pattern */}
                      <div
                        className="absolute inset-0 opacity-[0.18] pointer-events-none"
                        style={{
                          backgroundImage:
                            'radial-gradient(circle, #3D5A47 1px, transparent 1px)',
                          backgroundSize: '22px 22px',
                        }}
                      />

                      {/* Top brand strip */}
                      <div className="absolute top-6 left-0 right-0 flex flex-col items-center z-10">
                        <img src={brand.logo} alt={brand.name} className="h-7 w-auto" />
                        <span className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#3D5A47]/70 font-bold">
                          {product.series}
                        </span>
                      </div>

                      {/* Headline-spec badge — top-right */}
                      <div className="absolute top-5 right-5 z-10 bg-[#3D5A47] text-white rounded-2xl px-3 py-2 text-center shadow-lg">
                        <span className="block text-[9px] uppercase tracking-widest font-bold text-[#B5D43B]">
                          {headlineShort}
                        </span>
                        <div className="flex items-baseline justify-center leading-none mt-0.5">
                          <span className="text-3xl font-black">{product.headlineSpec.value}</span>
                          <span className="text-lg font-black">{product.headlineSpec.unit}</span>
                        </div>
                      </div>

                      {/* Soft floor shadow under product */}
                      <div className="absolute left-1/2 bottom-[18%] -translate-x-1/2 w-[55%] h-3 rounded-[50%] bg-[#3D5A47]/15 blur-md" />

                      {/* Product image */}
                      {product.image && (
                        <img
                          src={product.image}
                          alt={`${product.name} product render`}
                          className="absolute inset-x-10 top-[18%] bottom-[22%] m-auto max-h-full max-w-full object-contain"
                          style={{
                            filter: 'drop-shadow(0 18px 18px rgba(26,40,32,0.18))',
                            mixBlendMode: product.imageBlendMode || 'multiply',
                          }}
                          data-testid="product-render-image"
                        />
                      )}

                      {/* Bottom info strip */}
                      <div className="absolute bottom-0 left-0 right-0 px-5 py-3 z-10 bg-gradient-to-t from-[#1a2820] via-[#1a2820]/95 to-transparent text-center">
                        <p className="text-xs text-white/95 mb-1 font-medium">{product.heroBadge}</p>
                        <p className="text-[10px] text-[#B5D43B]/80 uppercase tracking-widest font-mono">
                          Model {product.model}
                        </p>
                      </div>
                    </div>

                    {/* Floating stat card — bottom-left overlap */}
                    <div className="absolute -bottom-6 -left-3 sm:-left-6 bg-white rounded-2xl shadow-xl p-5 border border-[#B5D43B]/40 max-w-[240px] z-20" data-testid="product-hero-stat-card">
                      <div className="flex items-center gap-2 mb-1">
                        <HeroStatIcon className="w-5 h-5 text-[#B5D43B]" />
                        <span className="text-xs font-semibold tracking-wide uppercase text-[#3D5A47]">
                          {heroStatCard.label}
                        </span>
                      </div>
                      {heroStatCard.value && (
                        <div className="text-3xl font-bold text-[#3D5A47]">
                          {heroStatCard.value}
                          {heroStatCard.unit && (
                            <span className="text-base font-medium text-gray-500">
                              {heroStatCard.unit}
                            </span>
                          )}
                        </div>
                      )}
                      {heroStatCard.sub && (
                        <p className="text-xs text-gray-600 mt-1">{heroStatCard.sub}</p>
                      )}
                    </div>

                    {/* Floating chip — top-left overlap */}
                    <div className="absolute -top-3 -left-3 bg-[#B5D43B] text-[#1a2820] rounded-full px-3 py-1.5 shadow-lg z-20 flex items-center gap-1.5">
                      <HeroChipIcon className="w-3.5 h-3.5" />
                      <span className="text-[11px] font-bold uppercase tracking-wider">
                        {heroChip.label}
                      </span>
                    </div>
                  </div>
                ) : (
                  // ── Dark hero variant (default — heat pumps, Fox ESS) ──
                  <div
                    className="relative w-full max-w-md aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl flex text-white"
                    style={{
                      background:
                        'radial-gradient(circle at 30% 20%, #4a6a55 0%, #2a4032 60%, #1a2820 100%)',
                    }}
                    data-testid="product-tank-hero"
                  >
                    <div
                      className="absolute inset-0 opacity-10 pointer-events-none"
                      style={{
                        backgroundImage:
                          'radial-gradient(circle, #B5D43B 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                      }}
                    />

                    <div className="absolute top-5 left-0 right-0 flex flex-col items-center z-10">
                      <div className="bg-white/95 rounded-lg px-3 py-1.5">
                        <img src={brand.logo} alt={brand.name} className="h-5 w-auto" />
                      </div>
                      <span className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#B5D43B] font-bold">
                        {product.series}
                      </span>
                    </div>

                    <div className="absolute top-5 right-5 z-10 bg-[#B5D43B] text-[#1a2820] rounded-2xl px-3 py-2 text-center shadow-lg">
                      <span className="block text-[9px] uppercase tracking-widest font-bold">{headlineShort}</span>
                      <div className="flex items-baseline justify-center leading-none">
                        <span className="text-3xl font-black">{product.headlineSpec.value}</span>
                        <span className="text-lg font-black">{product.headlineSpec.unit}</span>
                      </div>
                    </div>

                    {product.image && (
                      <img
                        src={product.image}
                        alt={`${product.name} product render`}
                        className="absolute inset-0 m-auto h-[78%] w-auto object-contain"
                        style={{ mixBlendMode: 'multiply' }}
                        data-testid="product-render-image"
                      />
                    )}

                    <div className="absolute bottom-4 left-0 right-0 text-center z-10 px-4">
                      <p className="text-xs text-white/80 mb-1">{product.heroBadge}</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-widest font-mono">
                        Model {product.model}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-[#3D5A47] mb-3">Key Features</h2>
            <p className="text-gray-600 mb-10 max-w-2xl">
              Every {product.series} unit is engineered around these next-generation hot-water highlights.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {product.keyFeatures.map((f, i) => {
                const Icon = featureIcon(f.title);
                return (
                  <div
                    key={i}
                    className="bg-gray-50 p-5 rounded-2xl hover:shadow-md hover:bg-white hover:border-[#B5D43B]/50 border border-transparent transition-all"
                    data-testid={`product-feature-${i}`}
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-3">
                      <Icon className="w-5 h-5 text-[#3D5A47]" />
                    </div>
                    <h3 className="font-semibold text-[#3D5A47] mb-1 text-sm">{f.title}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{f.detail}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Specifications */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-1">
                <h2 className="heading-2 text-[#3D5A47] mb-3">Technical Specifications</h2>
                <p className="text-gray-600 text-sm mb-6">
                  Full datasheet figures from the manufacturer's official brochure.
                </p>
                {brochureUrl && (
                  <a href={brochureUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="gap-2 border-[#3D5A47] text-[#3D5A47] hover:bg-[#3D5A47] hover:text-white">
                      <Download className="w-4 h-4" /> Full Brochure
                    </Button>
                  </a>
                )}
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <table className="w-full" data-testid="product-specs-table">
                    <tbody>
                      {product.specs.map((s, i) => (
                        <tr
                          key={i}
                          className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}
                        >
                          <td className="px-5 py-3 text-sm text-gray-600 font-medium w-1/2">
                            {s.label}
                          </td>
                          <td className="px-5 py-3 text-sm text-[#3D5A47] font-semibold">
                            {s.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Warranty */}
                <div className="mt-6 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="w-5 h-5 text-[#B5D43B]" />
                    <h3 className="font-bold text-[#3D5A47]">Manufacturer Warranty</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {product.warranty.map((w, i) => (
                      <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                        <p className="text-xs text-gray-500 mb-1">{w.component}</p>
                        <p className="text-lg font-bold text-[#3D5A47]">{w.term}</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-3 leading-relaxed">
                    *Extended labour warranty subject to terms set by the manufacturer and installer. Always refer to the official brochure and warranty card.
                  </p>
                  {warrantyUrl && (
                    <a
                      href={warrantyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium text-[#3D5A47] hover:text-[#B5D43B] underline underline-offset-2"
                      data-testid="product-warranty-card-download"
                    >
                      <Download className="w-3.5 h-3.5" /> View official manufacturer warranty document (PDF)
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Real-world installations — prefer product-specific photos, fall back to brand-level */}
        {(() => {
          const galleryImages = product.installationImages?.length
            ? product.installationImages
            : brand.installationImages;
          const isProductSpecific = !!product.installationImages?.length;
          if (!galleryImages?.length) return null;
          return (
            <section className="py-16 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-10">
                  <span className="inline-block text-[#B5D43B] font-semibold text-xs tracking-widest uppercase mb-2">
                    Real-World Installations
                  </span>
                  <h2 className="heading-2 text-[#3D5A47]">See it in the wild</h2>
                  <p className="text-gray-600 mt-2 max-w-2xl">
                    {product.installationCaption ||
                      (isProductSpecific
                        ? `Authentic ${brand.name} ${product.series} installations from real Australian homes.`
                        : `Authentic ${brand.name} ${product.series} installations and lifestyle imagery — straight from ${brand.name}'s own gallery.`)}
                  </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {galleryImages.map((src, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-2xl overflow-hidden bg-gray-100 group"
                      data-testid={`install-photo-${i}`}
                    >
                      <img
                        src={src}
                        alt={`${brand.name} ${product.series} real-world installation ${i + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          );
        })()}

        {/* Compare / Related */}
        {relatedProducts.length > 0 && (
          <section className="py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="heading-2 text-[#3D5A47] mb-3">Compare other models</h2>
              <p className="text-gray-600 mb-8">
                Different household size? Take a look at our other {category.name.toLowerCase()}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedProducts.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/products/${category.slug}/${p.slug}`}
                    className="group bg-gray-50 hover:bg-white border border-gray-200 hover:border-[#B5D43B]/50 hover:shadow-lg rounded-2xl p-5 flex items-center gap-5 transition-all"
                    data-testid={`related-product-${p.slug}`}
                  >
                    <div className="flex-shrink-0 w-24 h-24 rounded-xl bg-gradient-to-br from-[#3D5A47] to-[#2a4032] text-white flex flex-col items-center justify-center">
                      <span className="text-[9px] uppercase tracking-wider text-[#B5D43B]">{headlineShort}</span>
                      <span className="text-3xl font-black">{p.headlineSpec.value}</span>
                      <span className="text-sm font-bold text-[#B5D43B]">{p.headlineSpec.unit}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-[#3D5A47] group-hover:text-[#B5D43B] transition-colors">
                        {p.name}
                      </h3>
                      <p className="text-xs text-gray-400 font-mono mb-1">Model {p.model}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{p.tagline}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#B5D43B] group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">
              Ready to install the {product.name}?
            </h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Get a tailored quote with all federal STCs and state rebates applied upfront. Installed by our CEC-accredited team.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/get-estimate">
                <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2 font-semibold" data-testid="product-bottom-cta">
                  Get Free Estimate <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to={`/products/${category.slug}`}>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#3D5A47] gap-2">
                  <ArrowLeft className="w-4 h-4" /> All {category.name}
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Specifications, dimensions, COP and noise figures sourced from the manufacturer's official brochure and are subject to change without notice. Actual energy use, savings and rebate amounts depend on usage patterns, climate zone, installation conditions and applicable state/federal rebate program criteria at time of installation. Supa Energy Hub does not warrant any specific performance result beyond the manufacturer's published warranty and is not affiliated with {brand.name}. All trademarks are the property of their respective owners.
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetailPage;
